<template>
<div>
    <section class="pt-12 pt-md-14 pb-8">
        <div class="container">
            <div class="row justify-content-center text-center">
                <div class="col-12 col-md-10 col-lg-8">
                    <h1 class="display-3 fw-bold">Frequently Asked Questions</h1>
                    <p class="fs-lg text-muted">
                        Everything you need to know about {{ $state.siteName }}. Can't find an answer?
                        <a :href="`mailto:${$state.siteEmail}`">Email us</a> or ask in
                        <a :href="`${$state.siteTgBase}/${$state.siteTgSupport}`">Telegram</a>.
                    </p>
                </div>
            </div>
        </div>
    </section>

    <section class="pb-8 pb-md-10">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-12 col-lg-10">

                    <div class="mb-8" v-for="cat in categories" :key="cat.id">
                        <h2 class="fw-bold mb-4">{{ cat.name }}</h2>
                        <div class="accordion" :id="'acc-' + cat.id">
                            <div class="accordion-item" v-for="(q, i) in cat.questions" :key="i">
                                <h3 class="accordion-header">
                                    <button class="accordion-button collapsed"
                                            type="button"
                                            @click="toggle(cat.id + '-' + i)">
                                        {{ q.question }}
                                    </button>
                                </h3>
                                <div class="accordion-collapse collapse"
                                     :class="{ show: openItem === cat.id + '-' + i }">
                                    <div class="accordion-body text-muted" v-html="q.answer"></div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    </section>

    <!-- CTA -->
    <section class="py-8 py-md-10 cta-section">
        <div class="container text-center">
            <h2 class="fw-bold mb-3">Still have questions?</h2>
            <p class="text-muted mb-6">Our team typically responds within a few hours.</p>
            <div class="d-flex justify-content-center gap-3">
                <a :href="`mailto:${$state.siteEmail}`" class="btn btn-primary shadow lift">
                    <i class="fe fe-mail me-2"></i>Email Us
                </a>
                <a :href="`${$state.siteTgBase}/${$state.siteTgSupport}`" class="btn btn-outline-primary lift">
                    <i class="fe fe-send me-2"></i>Telegram
                </a>
            </div>
        </div>
    </section>
</div>
</template>

<script>
export default {
    data() {
        return {
            openItem: null,
        }
    },
    methods: {
        toggle(id) {
            this.openItem = this.openItem === id ? null : id
        }
    },
    computed: {
        docsUrl() {
            return `${this.$state.siteProto}://api.${this.$state.siteDomain}/docs`
        },
        categories() {
            return [
                {
                    name: 'Getting Started',
                    id: 'start',
                    questions: [
                        {
                            question: `What is ${this.$state.siteName}?`,
                            answer: `
                                <p>${this.$state.siteName} is a platform for accessing Instagram and TikTok data at scale. We offer three products:</p>
                                <ul>
                                    <li><strong><a href="/cache-api">Cache API</a></strong> — fast REST API for real-time access to profiles, posts, stories, and more</li>
                                    <li><strong><a href="/datasets">Datasets</a></strong> — ready-made bulk downloads of users, posts, hashtags, and locations</li>
                                    <li><strong><a href="/mcp">MCP Server</a></strong> — Model Context Protocol integration for Claude Desktop and other AI assistants</li>
                                </ul>
                            `
                        },
                        {
                            question: 'Is there a free trial?',
                            answer: `
                                <p>Yes. Every new account gets <strong>100 free API requests</strong> — no credit card required.
                                This is enough to test endpoints, evaluate data quality, and build a prototype before committing.</p>
                                <p>Just <a href="/registration">register</a> and start making requests immediately.</p>
                            `
                        },
                        {
                            question: 'Do I need an Instagram or TikTok account to use the API?',
                            answer: `<p>No. ${this.$state.siteName} handles all authentication internally. You only need a ${this.$state.siteName} API key — no social network credentials required.</p>`
                        },
                        {
                            question: 'What programming languages are supported?',
                            answer: `
                                <p>Our Cache API is a REST API — it works with any language that can make HTTP requests.
                                See our <a href="${this.docsUrl}">interactive API docs</a> for ready-to-copy code examples in Python, JavaScript, cURL, PHP, Go, and more.</p>
                            `
                        },
                    ]
                },
                {
                    name: 'Products',
                    id: 'products',
                    questions: [
                        {
                            question: 'How does the Cache API work?',
                            answer: `
                                <p>Our <a href="/cache-api">Cache API</a> provides fast access to Instagram and TikTok profiles, posts, stories, followers, and more.
                                Data is cached on our infrastructure and regularly refreshed. You get instant responses without hitting rate limits,
                                and each record includes an <code>updated_at</code> timestamp so you know exactly how fresh it is.</p>
                            `
                        },
                        {
                            question: 'What data is available in datasets?',
                            answer: `
                                <p>Our <a href="/datasets">Datasets</a> include Instagram and TikTok users, posts, comments, hashtags, and locations — bulk downloads ready to use.
                                You can also build custom datasets by specifying hashtags, locations, or usernames.</p>
                            `
                        },
                        {
                            question: 'Do you offer an API for AI tools?',
                            answer: `
                                <p>Yes. Our <a href="/mcp">MCP Server</a> implements the Model Context Protocol, letting Claude Desktop and other AI assistants
                                query Instagram and TikTok data directly. Connect once and your AI can lookup profiles, posts, and analytics on demand.</p>
                            `
                        },
                        {
                            question: 'How fresh is the data?',
                            answer: `
                                <p>Cache API data is updated regularly — popular profiles and recent posts refresh more frequently than long-tail content.
                                Each record includes an <code>updated_at</code> timestamp so you can filter or re-fetch if needed.</p>
                            `
                        },
                        {
                            question: 'Can I access private accounts?',
                            answer: `<p>No. ${this.$state.siteName} only provides access to <strong>public</strong> Instagram and TikTok data.</p>`
                        },
                    ]
                },
                {
                    name: 'Pricing & Billing',
                    id: 'pricing',
                    questions: [
                        {
                            question: 'What requests do you charge for?',
                            answer: `
                                <p>We charge for any successful response with HTTP status <strong>200, 403, or 404</strong>.
                                Server errors (5xx) are <strong>free</strong> — you never pay for our mistakes.</p>
                            `
                        },
                        {
                            question: 'Will my funds expire at the end of the month?',
                            answer: `<p>No. Your balance never expires and there are no monthly fees. We only deduct for successful requests. If you stop making requests, your balance remains unchanged indefinitely.</p>`
                        },
                        {
                            question: 'Do you have plans for high-volume usage?',
                            answer: `
                                <p>Yes. We offer volume discounts for clients making over a million requests per month. Contact us via
                                <a href="mailto:${this.$state.siteEmail}">${this.$state.siteEmail}</a> or
                                <a href="${this.$state.siteTgBase}/${this.$state.siteTgSupport}">Telegram</a> to discuss custom pricing.</p>
                            `
                        },
                        {
                            question: 'What payment methods do you accept?',
                            answer: `
                                <p>We accept credit cards (Visa, Mastercard) and cryptocurrency (USDT TRC-20/ERC-20, ETH, BNB). Minimum deposit is $50.
                                Top up anytime from your billing page.</p>
                            `
                        },
                        {
                            question: 'Can I get a refund?',
                            answer: `<p>Yes. See our <a href="/help/refund-policy">Refund Policy</a> for details.</p>`
                        },
                    ]
                },
                {
                    name: 'Technical',
                    id: 'tech',
                    questions: [
                        {
                            question: 'Are there rate limits?',
                            answer: `<p>We handle all throttling, proxy rotation, and session management internally. For high-traffic use cases, <a href="${this.$state.siteTgBase}/${this.$state.siteTgSupport}">contact support</a> to arrange a higher throughput plan.</p>`
                        },
                        {
                            question: 'How do I handle pagination?',
                            answer: `
                                <p>Most list endpoints return a cursor (<code>end_cursor</code>, <code>next_page_id</code>, or similar) alongside the data.
                                Pass it as a parameter in the next request to get the next page. When the cursor is <code>null</code> or empty, you've reached the end.</p>
                            `
                        },
                    ]
                },
            ]
        }
    },
}
</script>

<style lang="scss" scoped>
@import '../scss/theme/variables';

.accordion-item {
    background: transparent;
    border: none;
    border-bottom: 1px solid $graphite-light;
}

.accordion-button {
    font-weight: $font-weight-bold;
    font-size: 1.05rem;
    padding: 1.25rem 0;
    background: transparent;
    box-shadow: none;
    color: $gray-200;
}

.accordion-button:not(.collapsed) {
    background: transparent;
    color: $primary;
    box-shadow: none;
}

.accordion-button::after {
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%2300d4aa'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e");
}

.accordion-button:focus {
    box-shadow: none;
}

.accordion-body {
    padding: 0 0 1.25rem 0;
    line-height: 1.7;
    color: $gray-400;
}

.accordion-body :deep(p:last-child) {
    margin-bottom: 0;
}

.accordion-body :deep(a) {
    color: $primary;
}

.accordion-body :deep(code) {
    background: $graphite-light;
    color: $gray-200;
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
}

.accordion-collapse {
    transition: none;
}

.cta-section {
    background: rgba($graphite-light, 0.3);
    border-top: 1px solid $graphite-light;
}
</style>
